import React, { FC, useState, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Textboxstar from '../../svg/textboxstar';
import SendChatIcon from '../../svg/SendChatIcon';
import Whatsapp from '../../svg/Whatsapp'; // Add this import
import WhatsAppButton from '../../component/WhatsAppButton';
import { useProfileData } from '../../hooks/useProfileData';
import { useAuth } from '../../hooks/useAuth';
import { useProductData } from '../../hooks/useProductData';

interface TextChatInputProps {
  sendDisabled: React.RefObject<boolean>;
  disabled: boolean;
  isTyping: boolean;
  handleMessageSend: (
    userMessage: string,
    firstMessage?: boolean,
    isVoiceMessage?: boolean
  ) => Promise<any>;
  isProductChatPage?: boolean;
}

const TextChatInput: FC<TextChatInputProps> = (props) => {
  const {
    sendDisabled,
    disabled,
    isTyping,
    handleMessageSend,
    isProductChatPage,
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const [messageVal, setMessageVal] = useState<string>('');
  const theme = useTheme();
  const { creatorProfileData } = useProfileData();
  const { productInfo } = useProductData();
  const { authUser, isAnonymousUser } = useAuth();

  useEffect(() => {
    inputRef?.current?.focus();
  }, [isTyping]); //eslint-disable-line react-hooks/exhaustive-deps

  const getRoomId = () => {
    if (authUser?.documentId) {
      if (isProductChatPage && productInfo) {
        return `${creatorProfileData?.userId}.${authUser?.documentId}.product_${productInfo?.id}`;
      }
      return `${creatorProfileData?.userId}.${authUser?.documentId}`;
    } else {
      if (isProductChatPage && productInfo) {
        return `${creatorProfileData?.userId}.anonymous-user.product_${productInfo?.id}`;
      }
      return `${creatorProfileData?.userId}.anonymous-user`;
    }
  };

  const roomId = getRoomId();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        margin: '8px 16px',
        width: '100%',
      }}
    >
      <WhatsAppButton variant='mobile' roomId={roomId} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '50px',
          backgroundColor: theme.palette.background.default,
          flexGrow: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1.5, mr: 1 }}>
          <Textboxstar />
        </Box>
        <TextField
          id='text-input-chat-message'
          autoComplete='off'
          inputRef={inputRef}
          value={messageVal}
          disabled={disabled}
          placeholder='Ask a question'
          variant='standard'
          fullWidth
          onChange={(e) => {
            sendDisabled.current = e.target.value.length === 0;
            setMessageVal(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !sendDisabled.current && !e.shiftKey) {
              e.preventDefault();
              if (!isTyping) {
                handleMessageSend(messageVal.trim());
                sendDisabled.current = true;
                setMessageVal('');
              }
            }
          }}
          onPaste={(e) => {
            e.preventDefault();
            if (e.clipboardData) {
              let content = e.clipboardData.getData('text/plain');
              sendDisabled.current = (messageVal + content).length === 0;
              setMessageVal((prev) => prev + content);
            }
          }}
          sx={{ flexGrow: 1 }}
          slotProps={{
            input: {
              disableUnderline: true,
              style: { padding: '0px', height: '36px' },
            },
          }}
        />

        {/* Right Icon - Send Button */}
        <IconButton
          color='primary'
          disabled={sendDisabled.current || isTyping}
          onClick={() => {
            if (!sendDisabled.current && !isTyping) {
              handleMessageSend(messageVal.trim());
              sendDisabled.current = true;
              setMessageVal('');
            }
          }}
        >
          <SendChatIcon isTextEntered={!!messageVal.trim()} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default TextChatInput;
